// Code Slots, from React Bits (https://reactbits.dev), MIT + Commons Clause License
// Condition v1.0. Copyright (c) 2026 David Haz. Used inside this product; not
// redistributed as a component. Adapted to the Chosen tokens.
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import {
  animate,
  motion,
  motionValue,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type MotionValue,
} from 'motion/react';
import { CheckIcon } from '@phosphor-icons/react/Check';
import { cn } from '../../../utils/cn';

export type CodeSlotsStatus = 'error' | 'idle' | 'success';

export interface CodeSlotsProps {
  /**
   * Accessible name of the hidden input. Defaults to "One-time code".
   */
  ariaLabel?: string;

  /**
   * Classes merged onto the root.
   */
  className?: string;

  /**
   * Digits typed so far when uncontrolled.
   */
  defaultValue?: string;

  /**
   * Whether typing is refused.
   */
  isDisabled?: boolean;

  /**
   * Whether digits show as dots.
   */
  isMasked?: boolean;

  /**
   * How many digits; 6 by default.
   */
  length?: number;

  /**
   * Whether the input takes focus on mount.
   */
  shouldAutoFocus?: boolean;

  /**
   * Idle, an error (the slots drain), or accepted (the green wash and check).
   */
  status?: CodeSlotsStatus;

  /**
   * Test hook placed on the root.
   */
  testId?: string;

  /**
   * Digits when controlled.
   */
  value?: string;

  /**
   * Called with the digits on every change, including `''` when an error drain
   * empties the slots.
   */
  onChange?: (code: string) => void;

  /**
   * Called once with the full code when the last slot fills.
   */
  onComplete?: (code: string) => void;
}

interface SlotProps {
  /**
   * The digit (or mask dot) in the slot; empty when the slot is empty.
   */
  char: string;

  /**
   * 0 at rest, 1 once the digit has sunk under the success wash.
   */
  dropValue: MotionValue<number>;

  /**
   * 0 empty, 1 filled; springs between the two.
   */
  fillValue: MotionValue<number>;

  /**
   * Whether the caret sits on this slot.
   */
  isActive: boolean;
}

interface Springs {
  /**
   * One sink value per slot.
   */
  drops: MotionValue<number>[];

  /**
   * One fill value per slot.
   */
  fills: MotionValue<number>[];
}

const SLOT_SIZE = 44;
const GAP = 8;
const RADIUS = 12;
const BOUNCE = 0.2;
const SETTLE = 0.3;
const RISE = 8;
const CASCADE = 20;

const PITCH = SLOT_SIZE + GAP;
const HEIGHT = Math.round(SLOT_SIZE * 1.18);
const SINK = Math.round(HEIGHT * 0.5);
const WASH_RADIUS = Math.min(RADIUS, SLOT_SIZE / 2);
const CHECK_SIZE = Math.round(SLOT_SIZE * 0.6);

// The kit's `--ease-out-strong` curve, as motion takes one: it cannot read a CSS variable.
const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];
const WASH_IN = 0.3;
const WASH_OUT = 0.2;
const SINK_DELAY = 0.06;
const SINK_STEP = 0.03;
const CHECK_DELAY = 0.28;
const CHECK_RISE = 8;
const SINK_FADE = 0.6;

const ROOT_STYLE = {
  '--cs-accent': 'var(--color-primary)',
  '--cs-ink': 'var(--color-fg-1)',
  '--cs-slot': 'var(--color-surface-2)',
  '--cs-digit': 'var(--color-primary-foreground)',
  '--cs-danger': 'var(--color-bad)',
  '--cs-size': `${SLOT_SIZE}px`,
  '--cs-height': `${HEIGHT}px`,
  '--cs-gap': `${GAP}px`,
  '--cs-radius': `${WASH_RADIUS}px`,
  '--cs-font': `${Math.round(SLOT_SIZE * 0.5)}px`,
} as CSSProperties;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const digitsOf = (raw: string | undefined) => String(raw ?? '').replace(/\D/g, '');
const toSlots = (raw: string | undefined, count: number) => {
  const digits = digitsOf(raw).slice(0, count);
  return Array.from({ length: count }, (_, index) => digits[index] ?? '');
};
const firstEmptyOf = (slots: string[]) => {
  const index = slots.indexOf('');
  return index === -1 ? slots.length - 1 : index;
};
const isFull = (slots: string[]) => slots.every(Boolean);
const createSprings = (count: number, slots: string[], status: CodeSlotsStatus): Springs => ({
  drops: Array.from({ length: count }, () => motionValue(status === 'success' ? 1 : 0)),
  fills: Array.from({ length: count }, (_, index) => motionValue(slots[index] ? 1 : 0)),
});

/**
 * A one-time code field: one hidden input under a row of slots.
 */
function CodeSlots({
  ariaLabel = 'One-time code',
  className,
  defaultValue = '',
  isDisabled = false,
  isMasked = false,
  length: lengthProp = 6,
  shouldAutoFocus = false,
  status = 'idle',
  testId,
  value,
  onChange,
  onComplete,
}: CodeSlotsProps): ReactElement {
  const length = Number.isInteger(lengthProp) && lengthProp > 0 ? lengthProp : 6;
  const uid = useId();
  const reduce = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState(() => toSlots(value ?? defaultValue, length));
  const [active, setActive] = useState(() => firstEmptyOf(slots));
  const [isFocused, setIsFocused] = useState(false);
  const [isVeiled, setIsVeiled] = useState(status === 'success');
  const [springs, setSprings] = useState(() => createSprings(length, slots, status));
  const [springsLength, setSpringsLength] = useState(length);
  const activeValue = useMotionValue(active);
  const openValue = useMotionValue(status === 'success' ? 1 : 0);
  const checkValue = useMotionValue(status === 'success' ? 1 : 0);
  const glide = useRef<Set<number>>(new Set());
  const target = useRef<number[]>([]);
  const draining = useRef(false);
  const drainTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const statusRef = useRef(status);
  const emitted = useRef(digitsOf(value ?? defaultValue).slice(0, length));
  const slotsRef = useRef(slots);

  if (springsLength !== length) {
    setSpringsLength(length);
    setSprings(createSprings(length, slots, status));
  }
  if (status === 'success' && !isVeiled) setIsVeiled(true);
  if (status !== 'success' && reduce && isVeiled) setIsVeiled(false);
  const { drops, fills } = springs;

  const caretX = useTransform(() => {
    const activeIndex = activeValue.get();
    let offset = activeIndex * PITCH;
    for (let index = 0; index < fills.length; index++) {
      const fill = clamp01(fills[index]?.get() ?? 0);
      if (!glide.current.has(index)) continue;
      const to = target.current[index];
      if (to === undefined || fill === clamp01(to)) {
        glide.current.delete(index);
        continue;
      }
      offset += index < activeIndex ? -(1 - fill) * PITCH : fill * PITCH;
    }
    return Math.min(Math.max(offset, 0), (fills.length - 1) * PITCH);
  });
  const caretTransform = useTransform(caretX, (offset: number) => `translateX(${offset}px)`);
  const washClip = useTransform(
    openValue,
    (open: number) => `inset(0 ${(1 - clamp01(open)) * 50}% round ${WASH_RADIUS}px)`,
  );
  const checkTransform = useTransform(
    checkValue,
    (check: number) =>
      `translateY(${(1 - check) * CHECK_RISE}px) scale(${0.85 + 0.15 * Math.max(check, 0)})`,
  );
  const checkOpacity = useTransform(checkValue, clamp01);

  const view =
    slots.length === length ? slots : Array.from({ length }, (_, index) => slots[index] ?? '');
  const filledCount = view.filter(Boolean).length;
  const isAccepted = status === 'success';
  const isInvalid = status === 'error';
  const isLocked = isDisabled || isAccepted;
  const isCaretShown =
    isFocused && !isDisabled && !isVeiled && !isAccepted && (isInvalid || !view[active]);
  const announcement = isAccepted ? 'Code accepted' : `${filledCount} of ${length} digits entered`;
  const countId = `${uid}-count`;
  const focusedFlag = isFocused ? '' : undefined;
  const disabledFlag = isDisabled ? '' : undefined;
  const caretFlag = isCaretShown ? '' : undefined;
  const washStyle = { clipPath: washClip };
  const checkStyle = { opacity: checkOpacity, transform: checkTransform };
  const caretStyle = { transform: caretTransform };

  const drive = useCallback(
    (index: number, to: number, delayMs = 0) => {
      const fillValue = fills[index];
      if (!fillValue) return;
      target.current[index] = to;
      if (reduce) {
        fillValue.jump(to);
        return;
      }
      animate(fillValue, to, {
        bounce: BOUNCE,
        delay: delayMs / 1000,
        duration: SETTLE,
        type: 'spring',
      });
    },
    [fills, reduce],
  );

  const land = useCallback(
    (index: number, delayMs = 0) => {
      const fillValue = fills[index];
      if (fillValue && fillValue.get() > 0) fillValue.jump(0);
      drive(index, 1, delayMs);
    },
    [drive, fills],
  );

  const moveActive = useCallback(
    (next: number, crossed: number[]) => {
      crossed.forEach((index) => glide.current.add(index));
      activeValue.jump(next);
      setActive(next);
    },
    [activeValue],
  );

  const jumpActive = useCallback(
    (next: number) => {
      glide.current.clear();
      activeValue.jump(next);
      setActive(next);
    },
    [activeValue],
  );

  const commit = useCallback(
    (next: string[]) => {
      const previous = slotsRef.current;
      slotsRef.current = next;
      setSlots(next);
      const code = next.join('');
      emitted.current = code;
      onChange?.(code);
      if (!isFull(previous) && isFull(next)) onComplete?.(code);
    },
    [onChange, onComplete],
  );

  const insert = useCallback(
    (raw: string, from: number) => {
      const digits = digitsOf(raw);
      if (!digits) return;
      const next = [...slotsRef.current];
      const crossed: number[] = [];
      const step = reduce ? 0 : CASCADE;
      let index = from;
      for (const digit of digits) {
        if (index >= length) break;
        next[index] = digit;
        land(index, (index - from) * step);
        crossed.push(index);
        index += 1;
      }
      if (!crossed.length) return;
      commit(next);
      moveActive(Math.min(index, length - 1), crossed);
    },
    [commit, land, length, moveActive, reduce],
  );

  const clearSlot = useCallback(
    (index: number, shouldStepBack = false) => {
      if (!slotsRef.current[index]) {
        if (shouldStepBack) jumpActive(index);
        return;
      }
      const next = [...slotsRef.current];
      next[index] = '';
      drive(index, 0);
      commit(next);
      if (shouldStepBack) moveActive(index, [index]);
    },
    [commit, drive, jumpActive, moveActive],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isLocked || draining.current || event.metaKey || event.ctrlKey || event.altKey) return;
      const { key } = event;
      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        insert(key, active);
        return;
      }
      switch (key) {
        case 'ArrowLeft':
          jumpActive(Math.max(active - 1, 0));
          break;
        case 'ArrowRight':
          jumpActive(Math.min(active + 1, length - 1));
          break;
        case 'Backspace':
          if (slotsRef.current[active]) clearSlot(active);
          else if (active > 0) clearSlot(active - 1, true);
          break;
        case 'Delete':
          clearSlot(active);
          break;
        case 'End':
          jumpActive(length - 1);
          break;
        case 'Home':
          jumpActive(0);
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    [active, clearSlot, insert, isLocked, jumpActive, length],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      if (isLocked || draining.current) return;
      event.preventDefault();
      insert(event.clipboardData.getData('text'), active);
    },
    [active, insert, isLocked],
  );

  const handleInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isLocked || draining.current) return;
      const digits = digitsOf(event.target.value);
      if (!digits) return;
      insert(digits, digits.length === 1 ? active : 0);
    },
    [active, insert, isLocked],
  );

  const handleRowMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      event.preventDefault();
      const row = rowRef.current;
      if (row && !draining.current && !isAccepted) {
        const rect = row.getBoundingClientRect();
        const zoom = rect.width / (row.offsetWidth || rect.width) || 1;
        const index = Math.floor((event.clientX - rect.left) / zoom / PITCH);
        jumpActive(Math.max(0, Math.min(index, firstEmptyOf(slotsRef.current))));
      }
      inputRef.current?.focus();
    },
    [isAccepted, isDisabled, jumpActive],
  );

  const handleFocus = useCallback(() => setIsFocused(true), []);

  const handleBlur = useCallback(() => setIsFocused(false), []);

  const syncLength = useEffectEvent(() => {
    glide.current.clear();
    target.current = [];
    const next = Array.from({ length }, (_, index) => slotsRef.current[index] ?? '');
    slotsRef.current = next;
    setSlots(next);
    jumpActive(firstEmptyOf(next));
    const code = next.join('');
    if (code === emitted.current) return;
    emitted.current = code;
    onChange?.(code);
  });

  const syncValue = useEffectEvent(() => {
    if (value === undefined) return;
    const clean = digitsOf(value).slice(0, length);
    if (clean === emitted.current) return;
    emitted.current = clean;
    const previous = slotsRef.current;
    const next = toSlots(clean, length);
    const isHidden = statusRef.current === 'success';
    const landing: number[] = [];
    const leaving: number[] = [];
    next.forEach((digit, index) => {
      if (digit === previous[index]) return;
      if (digit) landing.push(index);
      else leaving.push(index);
    });
    const step = reduce || isHidden ? 0 : CASCADE;
    landing.forEach((index, order) => land(index, order * step));
    leaving.reverse().forEach((index, order) => {
      if (!isHidden) {
        drive(index, 0, order * step);
        return;
      }
      target.current[index] = 0;
      fills[index]?.jump(0);
      drops[index]?.jump(0);
    });
    slotsRef.current = next;
    setSlots(next);
    moveActive(firstEmptyOf(next), [...landing, ...leaving]);
    if (!isFull(previous) && isFull(next)) onComplete?.(clean);
  });

  const playStatus = useEffectEvent(() => {
    const was = statusRef.current;
    if (status === 'success') {
      if (reduce) {
        openValue.jump(1);
        drops.forEach((dropValue) => dropValue.jump(1));
        checkValue.jump(1);
        return;
      }
      animate(openValue, 1, { duration: WASH_IN, ease: EASE_OUT });
      drops.forEach((dropValue, order) =>
        animate(dropValue, 1, {
          bounce: 0,
          delay: SINK_DELAY + order * SINK_STEP,
          duration: 0.3,
          type: 'spring',
        }),
      );
      animate(checkValue, 1, {
        bounce: BOUNCE,
        delay: CHECK_DELAY,
        duration: 0.35,
        type: 'spring',
      });
      return;
    }
    if (was !== 'success') return;
    if (reduce) {
      openValue.jump(0);
      checkValue.jump(0);
      drops.forEach((dropValue) => dropValue.jump(0));
      return;
    }
    animate(checkValue, 0, { duration: 0.15, ease: EASE_OUT });
    animate(openValue, 0, { delay: 0.06, duration: WASH_OUT, ease: EASE_OUT }).then(() => {
      if (openValue.get() === 0) setIsVeiled(false);
    });
    drops.forEach((dropValue) =>
      animate(dropValue, 0, { bounce: 0, delay: 0.1, duration: 0.3, type: 'spring' }),
    );
  });

  const drainOnError = useEffectEvent(() => {
    if (status !== 'error') return;
    const filled = slotsRef.current
      .map((digit, index) => (digit ? index : -1))
      .filter((index) => index >= 0);
    if (!filled.length) return;
    filled.reverse();
    draining.current = true;
    const step = reduce ? 0 : CASCADE;
    filled.forEach((index, order) => drive(index, 0, order * step));
    moveActive(
      0,
      slotsRef.current.map((_, index) => index),
    );
    clearTimeout(drainTimer.current);
    const drainMs = reduce ? 300 : (filled.length - 1) * step + SETTLE * 1000;
    drainTimer.current = setTimeout(() => {
      draining.current = false;
      commit(Array.from({ length }, () => ''));
    }, drainMs);
  });

  useEffect(() => syncLength(), [length]);

  useEffect(() => syncValue(), [value, length]);

  useEffect(() => playStatus(), [status]);

  useEffect(() => drainOnError(), [status]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => () => clearTimeout(drainTimer.current), []);

  useEffect(() => {
    if (shouldAutoFocus) inputRef.current?.focus();
  }, [shouldAutoFocus]);

  const classes = {
    root: cn('relative inline-block', className),
    row: cn(
      'group/row relative inline-flex cursor-text touch-manipulation gap-(--cs-gap) [-webkit-tap-highlight-color:transparent]',
      'transition-opacity duration-200',
      'data-disabled:cursor-not-allowed data-disabled:opacity-50',
    ),
    input: cn(
      'absolute inset-0 z-4 m-0 cursor-[inherit] appearance-none border-0 bg-transparent p-0',
      'text-base text-transparent caret-transparent opacity-0 outline-0',
    ),
    wash: cn(
      'pointer-events-none absolute inset-0 z-1 grid place-items-center rounded-(--cs-radius) bg-(--cs-accent) text-(--cs-digit)',
      'motion-reduce:opacity-0 motion-reduce:transition-opacity motion-reduce:duration-200 motion-reduce:in-data-[status=success]:opacity-100',
    ),
    check: cn(
      'grid place-items-center',
      'motion-reduce:transform-none! motion-reduce:transition-opacity motion-reduce:duration-150',
    ),
    caret: cn(
      'pointer-events-none absolute top-1/4 left-[calc(var(--cs-size)/2-0.75px)] z-3 h-1/2 w-[1.5px]',
      'opacity-0 data-show:opacity-100',
    ),
  };

  const renderSlots = () =>
    view.map((digit, index) => {
      const char = isMasked && digit ? '•' : digit;
      const isActive = isFocused && index === active;
      const fillValue = fills[index];
      const dropValue = drops[index];
      if (!fillValue || !dropValue) return null;
      return (
        <Slot
          // A slot is its position: the digit in it changes, the slot does not.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          char={char}
          dropValue={dropValue}
          fillValue={fillValue}
          isActive={isActive}
        />
      );
    });

  return (
    <div
      className={classes.root}
      data-testid={testId}
      style={ROOT_STYLE}
    >
      <div
        ref={rowRef}
        className={classes.row}
        data-disabled={disabledFlag}
        data-focused={focusedFlag}
        data-status={status}
        role="presentation"
        onMouseDown={handleRowMouseDown}
      >
        <input
          ref={inputRef}
          aria-describedby={countId}
          aria-invalid={isInvalid}
          aria-label={ariaLabel}
          autoComplete="one-time-code"
          className={classes.input}
          disabled={isDisabled}
          inputMode="numeric"
          maxLength={length}
          pattern="[0-9]*"
          readOnly={isAccepted}
          type="text"
          value=""
          onBlur={handleBlur}
          onChange={handleInput}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
        {renderSlots()}
        <motion.span
          aria-hidden="true"
          className={classes.wash}
          style={washStyle}
        >
          <motion.span
            className={classes.check}
            style={checkStyle}
          >
            <CheckIcon
              size={CHECK_SIZE}
              weight="bold"
            />
          </motion.span>
        </motion.span>
        <motion.span
          aria-hidden="true"
          className={classes.caret}
          data-show={caretFlag}
          style={caretStyle}
        >
          <span
            key={active}
            className="animate-blink block size-full bg-(--cs-ink) motion-reduce:animate-none"
          />
        </motion.span>
      </div>
      <span
        aria-live="polite"
        className="sr-only"
        id={countId}
      >
        {announcement}
      </span>
    </div>
  );
}

function Slot({ char, dropValue, fillValue, isActive }: SlotProps) {
  const [shown, setShown] = useState(char);
  if (char && char !== shown) setShown(char);
  const fill = useTransform(fillValue, (progress: number) => `scale(${Math.max(progress, 0)})`);
  const lift = useTransform(
    [fillValue, dropValue],
    ([progress = 0, drop = 0]: number[]) =>
      `translateY(${(1 - progress) * RISE + Math.max(drop, 0) * SINK}px)`,
  );
  const ink = useTransform(
    [fillValue, dropValue],
    ([progress = 0, drop = 0]: number[]) => clamp01(progress) * (1 - clamp01(drop / SINK_FADE)),
  );

  const activeFlag = isActive ? '' : undefined;
  const filledFlag = char ? '' : undefined;
  const fillStyle = { transform: fill };
  const digitStyle = { opacity: ink, transform: lift };

  const classes = {
    slot: cn(
      'relative h-(--cs-height) w-(--cs-size) overflow-hidden rounded-(--cs-radius) bg-(--cs-slot) select-none',
      'transition-colors duration-200',
      'data-active:bg-[color-mix(in_srgb,var(--cs-ink)_8%,var(--cs-slot))]',
      'in-data-[status=error]:bg-[color-mix(in_srgb,var(--cs-danger)_20%,var(--cs-slot))]',
      'in-data-[status=error]:data-active:bg-[color-mix(in_srgb,var(--cs-danger)_20%,var(--cs-slot))]',
      'pointer-fine:group-hover/row:in-data-[status=idle]:not-in-data-disabled:not-data-active:bg-[color-mix(in_srgb,var(--cs-ink)_4%,var(--cs-slot))]',
    ),
    fill: cn(
      'absolute inset-0 origin-center rounded-[inherit] bg-(--cs-accent)',
      'transition-colors duration-200 in-data-[status=error]:bg-(--cs-danger)',
    ),
    digit: cn(
      'absolute inset-0 z-2 grid place-items-center text-(length:--cs-font) leading-none font-semibold text-(--cs-digit) tabular-nums',
      'motion-reduce:transform-none! motion-reduce:transition-opacity motion-reduce:duration-150',
    ),
  };

  const renderDigit = () => {
    if (!shown) return null;
    return (
      <motion.span
        className={classes.digit}
        style={digitStyle}
      >
        {shown}
      </motion.span>
    );
  };

  return (
    <span
      aria-hidden="true"
      className={classes.slot}
      data-active={activeFlag}
      data-filled={filledFlag}
    >
      <motion.span
        className={classes.fill}
        style={fillStyle}
      />
      {renderDigit()}
    </span>
  );
}

export { CodeSlots };
